/** Distinct editorial, measured-sequence and listening evidence; never infer approval. */
type Row = Record<string, unknown>;
const object = (value: unknown): Row =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Row)
    : {};

export type DirectorReviewSummary = {
  scope: "editorial" | "measured_sequence";
  status: string;
  mode: string;
  route: string | null;
  inputDigest: string | null;
  journalKey: string | null;
  findings: {
    id: string;
    severity: string;
    explanation: string;
    change: string;
  }[];
};

export function readDirectorReview(
  value: unknown,
  scope: DirectorReviewSummary["scope"],
  expected?: { planDigest: string; compiledDigest: unknown },
): DirectorReviewSummary {
  const receipt = object(value);
  const result = object(receipt.result);
  let status =
    typeof receipt.status === "string" ? receipt.status : "not_reviewed";
  if (receipt.scope !== scope || !receipt.input_digest) status = "not_reviewed";
  if (
    expected &&
    receipt.scope === scope &&
    (receipt.plan_digest !== expected.planDigest ||
      !expected.compiledDigest ||
      receipt.compiled_digest !== expected.compiledDigest)
  )
    status = "stale";
  if (
    status === "approved" &&
    (result.verdict !== "approve" ||
      result.input_digest !== receipt.input_digest)
  )
    status = "unverified";
  return {
    scope,
    status,
    mode: typeof receipt.mode === "string" ? receipt.mode : "legacy",
    route: typeof receipt.route === "string" ? receipt.route : null,
    inputDigest:
      typeof receipt.input_digest === "string" ? receipt.input_digest : null,
    journalKey:
      typeof receipt.journal_key === "string" ? receipt.journal_key : null,
    findings: Array.isArray(result.findings)
      ? result.findings.map((value) => {
          const finding = object(value);
          return {
            id: String(finding.finding_id ?? ""),
            severity: String(finding.severity ?? ""),
            explanation: String(finding.explanation ?? ""),
            change: String(finding.requested_change ?? ""),
          };
        })
      : [],
  };
}
